import * as z from 'zod';

export const GetOrderRequest = z.object({
  _id: z.string().describe('ID of the order to retrieve.'),
  options: z
    .object({
      fieldMask: z
        .object({ paths: z.array(z.string()) })
        .describe(
          "Set of fields to retrieve.\nFields that aren't included in `fieldmaks.paths` are ignored.\nSee [the article about field masks](https://dev.wix.com/api/rest/restaurants/orders/supported-field-masks)\nfor more details."
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const GetOrderResponse = (() => {
  let comWixpressRestaurantsOrdersV3LineItemSchema: z.ZodType<any> = z.object({
    quantity: z
      .number()
      .int()
      .describe('Line item quantity.')
      .min(1)
      .max(100000)
      .optional(),
    price: z.string().describe('Line item price.').optional(),
    comment: z
      .string()
      .describe('Comment about the line item added by the customer.')
      .max(1000)
      .optional()
      .nullable(),
    dishOptions: z
      .array(
        z.object({
          name: z
            .string()
            .describe('Line item option name.')
            .max(1000)
            .optional()
            .nullable(),
          selectedChoices: z
            .array(z.lazy(() => comWixpressRestaurantsOrdersV3LineItemSchema))
            .min(0)
            .max(300)
            .optional(),
          minChoices: z
            .number()
            .int()
            .describe(
              'Minimum number of dish options the customer is required to choose.'
            )
            .min(0)
            .max(2147483647)
            .optional()
            .nullable(),
          maxChoices: z
            .number()
            .int()
            .describe(
              'Maximum number of dish options the customer is allowed to choose.'
            )
            .min(1)
            .max(2147483647)
            .optional()
            .nullable(),
          type: z
            .enum([
              'UNSPECIFIED_DISPLAY_TYPE',
              'SELECTION',
              'EXTRAS',
              'DESELECTION',
            ])
            .describe('Dish option type.')
            .optional(),
          availableChoices: z
            .array(
              z.object({
                itemId: z
                  .string()
                  .describe('Line item ID as defined in the catalog.')
                  .optional()
                  .nullable(),
                price: z
                  .string()
                  .describe('Line item price.')
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Line item name.')
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .min(1)
            .max(300)
            .optional(),
          defaultChoices: z.array(z.string()).min(0).max(300).optional(),
        })
      )
      .min(0)
      .max(300)
      .optional(),
    catalogReference: z
      .object({
        catalogItemId: z
          .string()
          .describe('Line item ID as defined in the catalog.')
          .optional(),
        catalogItemName: z
          .string()
          .describe('Line item name as defined in the catalog.')
          .max(1000)
          .optional()
          .nullable(),
        catalogItemDescription: z
          .string()
          .describe('Item description as defined in the catalog.')
          .max(1000)
          .optional()
          .nullable(),
        catalogItemMedia: z
          .string()
          .describe('Item media url as defined in the catalog.')
          .url()
          .optional()
          .nullable(),
      })
      .describe('References to the line item’s origin catalog.')
      .optional(),
  });
  return z.object({
    _id: z.string().describe('Order ID.').optional(),
    locationId: z
      .string()
      .describe('ID of the restaurant’s location.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe(
        'Order creation date and time in `yyyy-mm-ddThh:mm:sssZ` format.'
      )
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe(
        "Date and time of order's latest update in `yyyy-mm-ddThh:mm:sssZ` format."
      )
      .optional()
      .nullable(),
    comment: z
      .string()
      .describe('Additional note to the order added by the customer.')
      .max(1000)
      .optional()
      .nullable(),
    currency: z
      .string()
      .describe('Currency of the order.')
      .optional()
      .nullable(),
    status: z
      .enum([
        'UNSPECIFIED_ORDER_STATUS',
        'PENDING',
        'NEW',
        'ACCEPTED',
        'CANCELED',
        'FULFILLED',
      ])
      .describe('Current order status.')
      .optional(),
    lineItems: z
      .array(comWixpressRestaurantsOrdersV3LineItemSchema)
      .min(1)
      .max(300)
      .optional(),
    discounts: z
      .array(
        z.object({
          catalogDiscountId: z
            .string()
            .describe('Discount ID as defined in the catalog.')
            .optional(),
          appliedAmount: z.string().describe('Amount saved.').optional(),
          catalogDiscountType: z
            .enum([
              'UNSPECIFIED_TYPE',
              'OFF_ITEM',
              'OFF_ORDER',
              'OFF_ORDER_MANAGER_DISCOUNT',
            ])
            .describe('Discount type.')
            .optional(),
          catalogDiscountName: z
            .string()
            .describe('Discount name as defined in the catalog.')
            .min(1)
            .max(256)
            .optional(),
          catalogDiscountDescription: z
            .string()
            .describe(
              'Discount description as defined in the catalog.\n\nMax: 1,000 characters'
            )
            .min(1)
            .max(1000)
            .optional(),
        })
      )
      .min(0)
      .max(100)
      .optional(),
    coupon: z
      .object({
        code: z.string().describe('Coupon code.').optional(),
        _id: z
          .string()
          .describe('Coupon ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
      })
      .describe('Coupon applied to the order.')
      .optional(),
    payments: z
      .array(
        z.object({
          type: z
            .enum([
              'UNSPECIFIED_PAYMENT_TYPE',
              'CASH',
              'CREDIT',
              'DELIVERY_CLUB',
              'DELIVERY_COM',
              'BITPAY',
              'CELLARIX',
              'BITSOFGOLD',
              'MULTIPASS',
              'TENBIS',
              'PAYPAL',
              'MYSODEXO',
              'WIX_PAYMENTS',
            ])
            .describe('Payment type.')
            .optional(),
          amount: z
            .string()
            .describe(
              'Amount paid using this payment type. Only differs from total amount paid in case of split payments.'
            )
            .optional()
            .nullable(),
          method: z.string().describe('Payment method.').optional().nullable(),
          providerTransactionId: z
            .string()
            .describe(
              'Transaction ID.\nSee [Cashier API](https://dev.wix.com/api/rest/wix-cashier/payments/transaction) for more details.'
            )
            .optional()
            .nullable(),
        })
      )
      .min(1)
      .max(100)
      .optional(),
    fulfillment: z
      .intersection(
        z.object({
          type: z
            .enum([
              'UNSPECIFIED_FULFILLMENT_TYPE',
              'PICKUP',
              'DELIVERY',
              'DINE_IN',
            ])
            .describe('Fulfillment type.')
            .optional(),
          promisedTime: z
            .date()
            .describe('Latest delivery time promised by the restaurant.')
            .optional()
            .nullable(),
          asap: z
            .boolean()
            .describe(
              'Whether the order should be fulfilled as soon as possible. Defaults to `true`.'
            )
            .optional()
            .nullable(),
        }),
        z.xor([
          z.object({
            deliveryDetails: z.never().optional(),
            pickupDetails: z.never().optional(),
            dineInDetails: z.never().optional(),
          }),
          z.object({
            pickupDetails: z.never().optional(),
            dineInDetails: z.never().optional(),
            deliveryDetails: z
              .intersection(
                z.object({
                  address: z
                    .object({
                      formatted: z
                        .string()
                        .describe('Formatted delivery address.')
                        .max(250)
                        .optional()
                        .nullable(),
                      country: z
                        .string()
                        .describe('Country.')
                        .max(100)
                        .optional()
                        .nullable(),
                      city: z
                        .string()
                        .describe('City name.')
                        .max(100)
                        .optional()
                        .nullable(),
                      street: z
                        .string()
                        .describe('Street name.')
                        .max(100)
                        .optional()
                        .nullable(),
                      streetNumber: z
                        .string()
                        .describe('Street number.')
                        .max(100)
                        .optional()
                        .nullable(),
                      apt: z
                        .string()
                        .describe('Apartment number.')
                        .max(100)
                        .optional()
                        .nullable(),
                      floor: z
                        .string()
                        .describe('Floor.')
                        .max(100)
                        .optional()
                        .nullable(),
                      entrance: z
                        .string()
                        .describe('Entrance.')
                        .max(100)
                        .optional()
                        .nullable(),
                      zipCode: z
                        .string()
                        .describe('ZIP/postal code.')
                        .max(100)
                        .optional()
                        .nullable(),
                      countryCode: z
                        .string()
                        .describe(
                          'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                        )
                        .optional()
                        .nullable(),
                      onArrival: z
                        .enum([
                          'UNSPECIFIED_ON_ARRIVAL_TYPE',
                          'BUZZ_DOOR',
                          'CALL_ME',
                        ])
                        .describe(
                          'Special delivery instructions upon arrival to the address.'
                        )
                        .optional(),
                      approximate: z
                        .boolean()
                        .describe(
                          'Whether an approximate address is used. Defaults to `false`. In case of `false` a house number is required.'
                        )
                        .optional()
                        .nullable(),
                      comment: z
                        .string()
                        .describe(
                          'Delivery Instructions added by the customer.'
                        )
                        .max(500)
                        .optional()
                        .nullable(),
                      location: z
                        .object({
                          latitude: z
                            .number()
                            .describe('Address latitude.')
                            .optional()
                            .nullable(),
                          longitude: z
                            .number()
                            .describe('Address longitude.')
                            .optional()
                            .nullable(),
                        })
                        .describe('Geo coordinates of the address.')
                        .optional(),
                      addressLine2: z
                        .string()
                        .describe('Address Line 2.')
                        .optional()
                        .nullable(),
                      subdivision: z
                        .string()
                        .describe(
                          'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Delivery address.')
                    .optional(),
                  pickupInfo: z
                    .object({
                      windowStartTime: z
                        .date()
                        .describe(
                          'When a delivery is ready to be picked up. This is the start time of the pickup window.'
                        )
                        .optional()
                        .nullable(),
                      windowEndTime: z
                        .date()
                        .describe(
                          'When a delivery must be picked up by. This is the end time of the pickup window.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Information about the delivery pickup.')
                    .optional(),
                }),
                z.xor([
                  z.object({
                    restaurant: z.never().optional(),
                    externalProvider: z.never().optional(),
                  }),
                  z.object({
                    externalProvider: z.never().optional(),
                    restaurant: z
                      .object({
                        fee: z
                          .string()
                          .describe('Delivery fee.')
                          .optional()
                          .nullable(),
                      })
                      .describe('Delivery through the restaurant.'),
                  }),
                  z.object({
                    restaurant: z.never().optional(),
                    externalProvider: z
                      .object({
                        _id: z
                          .string()
                          .describe('ID of the external delivery provider.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        name: z
                          .string()
                          .describe('Name of the external delivery provider.')
                          .optional(),
                        customerFee: z
                          .string()
                          .describe('Delivery fee charged to the customer.')
                          .optional()
                          .nullable(),
                        commission: z
                          .string()
                          .describe(
                            'Fee paid by the restaurant to the external delivery provider.'
                          )
                          .optional()
                          .nullable(),
                        pickupTime: z
                          .date()
                          .describe('Order pickup time.')
                          .optional()
                          .nullable(),
                        dropOffTime: z
                          .date()
                          .describe('Order drop off time.')
                          .optional()
                          .nullable(),
                      })
                      .describe('Delivery through an external provider.'),
                  }),
                ])
              )
              .describe('Delivery details.'),
          }),
          z.object({
            deliveryDetails: z.never().optional(),
            dineInDetails: z.never().optional(),
            pickupDetails: z
              .object({
                fee: z
                  .string()
                  .describe('Pickup fee charged to the customer.')
                  .optional()
                  .nullable(),
                curbside: z
                  .object({
                    info: z
                      .string()
                      .describe('Additional information for curbside pickup.')
                      .min(0)
                      .max(50)
                      .optional(),
                  })
                  .describe('Curbside pickup method')
                  .optional(),
              })
              .describe('Pickup details.'),
          }),
          z.object({
            deliveryDetails: z.never().optional(),
            pickupDetails: z.never().optional(),
            dineInDetails: z
              .object({
                label: z
                  .string()
                  .describe(
                    'Label of dine-in information added by the restaurant, e.g. `table` or `booth`.'
                  )
                  .optional(),
                value: z
                  .string()
                  .describe(
                    'Value of dine-in information added by the restaurant, e.g. `#6`.'
                  )
                  .optional(),
              })
              .describe('Dine-in details'),
          }),
        ])
      )
      .describe('Order fulfillment information.')
      .optional(),
    customer: z
      .intersection(
        z.object({
          firstName: z.string().describe('First name.').optional(),
          lastName: z.string().describe('Last name.').optional(),
          phone: z.string().describe('Phone number.').optional().nullable(),
          email: z.string().describe('Email address.').optional(),
          contactId: z
            .string()
            .describe(
              "Customer's contact ID.\nSee [Contacts API](https://dev.wix.com/api/rest/contacts/contacts/contacts-v4) for more details."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        }),
        z.xor([
          z.object({
            memberId: z.never().optional(),
            visitorId: z.never().optional(),
          }),
          z.object({
            visitorId: z.never().optional(),
            memberId: z
              .string()
              .describe('Member ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
          }),
          z.object({
            memberId: z.never().optional(),
            visitorId: z
              .string()
              .describe('Visitor ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
          }),
        ])
      )
      .describe('Customer information.')
      .optional(),
    totals: z
      .object({
        subtotal: z
          .string()
          .describe(
            'Order subtotal. Calculated by substracting delivery fee, tax and discount from order total.'
          )
          .optional(),
        total: z.string().describe('Total order price.').optional(),
        delivery: z
          .string()
          .describe('Total delivery fees charged to the customer.')
          .optional()
          .nullable(),
        tax: z.string().describe('Total tax.').optional().nullable(),
        discount: z
          .string()
          .describe('Total discount amount.')
          .optional()
          .nullable(),
        loyaltySavings: z
          .string()
          .describe('Total amount saved through the Wix Loyalty program.')
          .optional()
          .nullable(),
        quantity: z
          .number()
          .int()
          .describe('Total number of line items.')
          .min(0)
          .optional(),
        tip: z.string().describe('Total tip.').optional().nullable(),
      })
      .describe('Order totals.')
      .optional(),
    activities: z
      .array(
        z.object({
          timestamp: z
            .date()
            .describe('Activity timestamp.')
            .optional()
            .nullable(),
          message: z
            .string()
            .describe('Optional message added during order activity.')
            .max(1000)
            .optional()
            .nullable(),
        })
      )
      .optional(),
    channelInfo: z
      .object({
        type: z
          .enum([
            'UNSPECIFIED_CHANNEL_TYPE',
            'WEB',
            'MOBILE_WEB',
            'MOBILE',
            'CALL_CENTER',
            'FACEBOOK',
            'TPA',
          ])
          .optional(),
      })
      .describe('Information about the sales channel that submitted the order.')
      .optional(),
    loyaltyInfo: z
      .object({
        rewardId: z
          .string()
          .describe('Associated Wix Loyalty reward ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        appliedAmount: z
          .string()
          .describe('Amount saved redeeming Wix Loyalty reward.')
          .optional()
          .nullable(),
        redeemedPoints: z
          .number()
          .int()
          .describe('Wix Loyalty points redeemed.')
          .min(0)
          .optional()
          .nullable(),
        transactionId: z
          .string()
          .describe('Associated Wix Loyalty transaction ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        estimatedAccountBalance: z
          .number()
          .int()
          .describe(
            'Wix Loyalty estimated account point balance.\nEqual to the sum of earned and adjusted points minus redeemed points.\nThe estimated point balance is the value before the order is completed. Following the purchase, the point balance is updated.'
          )
          .min(0)
          .optional()
          .nullable(),
        estimatedPointsEarned: z
          .number()
          .int()
          .describe(
            'Wix Loyalty estimated total earned points.\nThe estimated total earned points is the value before the order is completed. Following the purchase, the total is updated.'
          )
          .min(0)
          .optional()
          .nullable(),
        rewardRevision: z
          .string()
          .describe(
            'Associated Wix Loyalty reward revision number, which increments by 1 each time the loyalty account is updated.\n\nTo prevent conflicting changes, the current revision must be passed when updating the loyalty account.\nIgnored when creating an account.'
          )
          .optional()
          .nullable(),
      })
      .describe(
        'Information about the order’s loyalty points.\nLearn more about the [Wix Loyalty Program](https://dev.wix.com/api/rest/wix-loyalty-program/introduction).'
      )
      .optional(),
  });
})();
export const ListOrdersRequest = z.object({
  options: z
    .object({
      fieldMask: z
        .object({ paths: z.array(z.string()) })
        .describe(
          "Set of fields to retrieve.\nFields that aren't included in `fieldmaks.paths` are ignored.\nSee [the article about field masks](https://dev.wix.com/api/rest/restaurants/orders/supported-field-masks)\nfor more details."
        )
        .optional()
        .nullable(),
      status: z
        .enum([
          'UNSPECIFIED_ORDER_STATUS',
          'PENDING',
          'NEW',
          'ACCEPTED',
          'CANCELED',
          'FULFILLED',
        ])
        .optional(),
      createdDate: z
        .string()
        .describe(
          'Order creation date and time. Must include a `gte:` or `lte:` prefix, for example: `gte:2021-10-26T12:48:15Z`.'
        )
        .optional()
        .nullable(),
      delivered: z
        .boolean()
        .describe('Whether the order has been delivered.')
        .optional()
        .nullable(),
      locationIds: z.array(z.string()).optional(),
      sort: z
        .object({
          fieldName: z
            .enum(['CREATED_DATE', 'UPDATED_DATE', 'PROMISED_TIME'])
            .optional(),
          order: z.enum(['ASC', 'DESC']).optional(),
        })
        .describe('Sorting options.')
        .optional(),
      limit: z
        .number()
        .int()
        .describe(
          'Number of orders to return. The minimum limit is 1, the maximum 1000.'
        )
        .min(1)
        .max(1000)
        .optional()
        .nullable(),
    })
    .optional(),
});
export const ListOrdersResponse = (() => {
  let comWixpressRestaurantsOrdersV3LineItemSchema: z.ZodType<any> = z.object({
    quantity: z
      .number()
      .int()
      .describe('Line item quantity.')
      .min(1)
      .max(100000)
      .optional(),
    price: z.string().describe('Line item price.').optional(),
    comment: z
      .string()
      .describe('Comment about the line item added by the customer.')
      .max(1000)
      .optional()
      .nullable(),
    dishOptions: z
      .array(
        z.object({
          name: z
            .string()
            .describe('Line item option name.')
            .max(1000)
            .optional()
            .nullable(),
          selectedChoices: z
            .array(z.lazy(() => comWixpressRestaurantsOrdersV3LineItemSchema))
            .min(0)
            .max(300)
            .optional(),
          minChoices: z
            .number()
            .int()
            .describe(
              'Minimum number of dish options the customer is required to choose.'
            )
            .min(0)
            .max(2147483647)
            .optional()
            .nullable(),
          maxChoices: z
            .number()
            .int()
            .describe(
              'Maximum number of dish options the customer is allowed to choose.'
            )
            .min(1)
            .max(2147483647)
            .optional()
            .nullable(),
          type: z
            .enum([
              'UNSPECIFIED_DISPLAY_TYPE',
              'SELECTION',
              'EXTRAS',
              'DESELECTION',
            ])
            .describe('Dish option type.')
            .optional(),
          availableChoices: z
            .array(
              z.object({
                itemId: z
                  .string()
                  .describe('Line item ID as defined in the catalog.')
                  .optional()
                  .nullable(),
                price: z
                  .string()
                  .describe('Line item price.')
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Line item name.')
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .min(1)
            .max(300)
            .optional(),
          defaultChoices: z.array(z.string()).min(0).max(300).optional(),
        })
      )
      .min(0)
      .max(300)
      .optional(),
    catalogReference: z
      .object({
        catalogItemId: z
          .string()
          .describe('Line item ID as defined in the catalog.')
          .optional(),
        catalogItemName: z
          .string()
          .describe('Line item name as defined in the catalog.')
          .max(1000)
          .optional()
          .nullable(),
        catalogItemDescription: z
          .string()
          .describe('Item description as defined in the catalog.')
          .max(1000)
          .optional()
          .nullable(),
        catalogItemMedia: z
          .string()
          .describe('Item media url as defined in the catalog.')
          .url()
          .optional()
          .nullable(),
      })
      .describe('References to the line item’s origin catalog.')
      .optional(),
  });
  return z.object({
    orders: z
      .array(
        z.object({
          _id: z.string().describe('Order ID.').optional(),
          locationId: z
            .string()
            .describe('ID of the restaurant’s location.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe(
              'Order creation date and time in `yyyy-mm-ddThh:mm:sssZ` format.'
            )
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe(
              "Date and time of order's latest update in `yyyy-mm-ddThh:mm:sssZ` format."
            )
            .optional()
            .nullable(),
          comment: z
            .string()
            .describe('Additional note to the order added by the customer.')
            .max(1000)
            .optional()
            .nullable(),
          currency: z
            .string()
            .describe('Currency of the order.')
            .optional()
            .nullable(),
          status: z
            .enum([
              'UNSPECIFIED_ORDER_STATUS',
              'PENDING',
              'NEW',
              'ACCEPTED',
              'CANCELED',
              'FULFILLED',
            ])
            .describe('Current order status.')
            .optional(),
          lineItems: z
            .array(comWixpressRestaurantsOrdersV3LineItemSchema)
            .min(1)
            .max(300)
            .optional(),
          discounts: z
            .array(
              z.object({
                catalogDiscountId: z
                  .string()
                  .describe('Discount ID as defined in the catalog.')
                  .optional(),
                appliedAmount: z.string().describe('Amount saved.').optional(),
                catalogDiscountType: z
                  .enum([
                    'UNSPECIFIED_TYPE',
                    'OFF_ITEM',
                    'OFF_ORDER',
                    'OFF_ORDER_MANAGER_DISCOUNT',
                  ])
                  .describe('Discount type.')
                  .optional(),
                catalogDiscountName: z
                  .string()
                  .describe('Discount name as defined in the catalog.')
                  .min(1)
                  .max(256)
                  .optional(),
                catalogDiscountDescription: z
                  .string()
                  .describe(
                    'Discount description as defined in the catalog.\n\nMax: 1,000 characters'
                  )
                  .min(1)
                  .max(1000)
                  .optional(),
              })
            )
            .min(0)
            .max(100)
            .optional(),
          coupon: z
            .object({
              code: z.string().describe('Coupon code.').optional(),
              _id: z
                .string()
                .describe('Coupon ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
            })
            .describe('Coupon applied to the order.')
            .optional(),
          payments: z
            .array(
              z.object({
                type: z
                  .enum([
                    'UNSPECIFIED_PAYMENT_TYPE',
                    'CASH',
                    'CREDIT',
                    'DELIVERY_CLUB',
                    'DELIVERY_COM',
                    'BITPAY',
                    'CELLARIX',
                    'BITSOFGOLD',
                    'MULTIPASS',
                    'TENBIS',
                    'PAYPAL',
                    'MYSODEXO',
                    'WIX_PAYMENTS',
                  ])
                  .describe('Payment type.')
                  .optional(),
                amount: z
                  .string()
                  .describe(
                    'Amount paid using this payment type. Only differs from total amount paid in case of split payments.'
                  )
                  .optional()
                  .nullable(),
                method: z
                  .string()
                  .describe('Payment method.')
                  .optional()
                  .nullable(),
                providerTransactionId: z
                  .string()
                  .describe(
                    'Transaction ID.\nSee [Cashier API](https://dev.wix.com/api/rest/wix-cashier/payments/transaction) for more details.'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .min(1)
            .max(100)
            .optional(),
          fulfillment: z
            .intersection(
              z.object({
                type: z
                  .enum([
                    'UNSPECIFIED_FULFILLMENT_TYPE',
                    'PICKUP',
                    'DELIVERY',
                    'DINE_IN',
                  ])
                  .describe('Fulfillment type.')
                  .optional(),
                promisedTime: z
                  .date()
                  .describe('Latest delivery time promised by the restaurant.')
                  .optional()
                  .nullable(),
                asap: z
                  .boolean()
                  .describe(
                    'Whether the order should be fulfilled as soon as possible. Defaults to `true`.'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  deliveryDetails: z.never().optional(),
                  pickupDetails: z.never().optional(),
                  dineInDetails: z.never().optional(),
                }),
                z.object({
                  pickupDetails: z.never().optional(),
                  dineInDetails: z.never().optional(),
                  deliveryDetails: z
                    .intersection(
                      z.object({
                        address: z
                          .object({
                            formatted: z
                              .string()
                              .describe('Formatted delivery address.')
                              .max(250)
                              .optional()
                              .nullable(),
                            country: z
                              .string()
                              .describe('Country.')
                              .max(100)
                              .optional()
                              .nullable(),
                            city: z
                              .string()
                              .describe('City name.')
                              .max(100)
                              .optional()
                              .nullable(),
                            street: z
                              .string()
                              .describe('Street name.')
                              .max(100)
                              .optional()
                              .nullable(),
                            streetNumber: z
                              .string()
                              .describe('Street number.')
                              .max(100)
                              .optional()
                              .nullable(),
                            apt: z
                              .string()
                              .describe('Apartment number.')
                              .max(100)
                              .optional()
                              .nullable(),
                            floor: z
                              .string()
                              .describe('Floor.')
                              .max(100)
                              .optional()
                              .nullable(),
                            entrance: z
                              .string()
                              .describe('Entrance.')
                              .max(100)
                              .optional()
                              .nullable(),
                            zipCode: z
                              .string()
                              .describe('ZIP/postal code.')
                              .max(100)
                              .optional()
                              .nullable(),
                            countryCode: z
                              .string()
                              .describe(
                                'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                              )
                              .optional()
                              .nullable(),
                            onArrival: z
                              .enum([
                                'UNSPECIFIED_ON_ARRIVAL_TYPE',
                                'BUZZ_DOOR',
                                'CALL_ME',
                              ])
                              .describe(
                                'Special delivery instructions upon arrival to the address.'
                              )
                              .optional(),
                            approximate: z
                              .boolean()
                              .describe(
                                'Whether an approximate address is used. Defaults to `false`. In case of `false` a house number is required.'
                              )
                              .optional()
                              .nullable(),
                            comment: z
                              .string()
                              .describe(
                                'Delivery Instructions added by the customer.'
                              )
                              .max(500)
                              .optional()
                              .nullable(),
                            location: z
                              .object({
                                latitude: z
                                  .number()
                                  .describe('Address latitude.')
                                  .optional()
                                  .nullable(),
                                longitude: z
                                  .number()
                                  .describe('Address longitude.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Geo coordinates of the address.')
                              .optional(),
                            addressLine2: z
                              .string()
                              .describe('Address Line 2.')
                              .optional()
                              .nullable(),
                            subdivision: z
                              .string()
                              .describe(
                                'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                              )
                              .optional()
                              .nullable(),
                          })
                          .describe('Delivery address.')
                          .optional(),
                        pickupInfo: z
                          .object({
                            windowStartTime: z
                              .date()
                              .describe(
                                'When a delivery is ready to be picked up. This is the start time of the pickup window.'
                              )
                              .optional()
                              .nullable(),
                            windowEndTime: z
                              .date()
                              .describe(
                                'When a delivery must be picked up by. This is the end time of the pickup window.'
                              )
                              .optional()
                              .nullable(),
                          })
                          .describe('Information about the delivery pickup.')
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          restaurant: z.never().optional(),
                          externalProvider: z.never().optional(),
                        }),
                        z.object({
                          externalProvider: z.never().optional(),
                          restaurant: z
                            .object({
                              fee: z
                                .string()
                                .describe('Delivery fee.')
                                .optional()
                                .nullable(),
                            })
                            .describe('Delivery through the restaurant.'),
                        }),
                        z.object({
                          restaurant: z.never().optional(),
                          externalProvider: z
                            .object({
                              _id: z
                                .string()
                                .describe(
                                  'ID of the external delivery provider.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  'Name of the external delivery provider.'
                                )
                                .optional(),
                              customerFee: z
                                .string()
                                .describe(
                                  'Delivery fee charged to the customer.'
                                )
                                .optional()
                                .nullable(),
                              commission: z
                                .string()
                                .describe(
                                  'Fee paid by the restaurant to the external delivery provider.'
                                )
                                .optional()
                                .nullable(),
                              pickupTime: z
                                .date()
                                .describe('Order pickup time.')
                                .optional()
                                .nullable(),
                              dropOffTime: z
                                .date()
                                .describe('Order drop off time.')
                                .optional()
                                .nullable(),
                            })
                            .describe('Delivery through an external provider.'),
                        }),
                      ])
                    )
                    .describe('Delivery details.'),
                }),
                z.object({
                  deliveryDetails: z.never().optional(),
                  dineInDetails: z.never().optional(),
                  pickupDetails: z
                    .object({
                      fee: z
                        .string()
                        .describe('Pickup fee charged to the customer.')
                        .optional()
                        .nullable(),
                      curbside: z
                        .object({
                          info: z
                            .string()
                            .describe(
                              'Additional information for curbside pickup.'
                            )
                            .min(0)
                            .max(50)
                            .optional(),
                        })
                        .describe('Curbside pickup method')
                        .optional(),
                    })
                    .describe('Pickup details.'),
                }),
                z.object({
                  deliveryDetails: z.never().optional(),
                  pickupDetails: z.never().optional(),
                  dineInDetails: z
                    .object({
                      label: z
                        .string()
                        .describe(
                          'Label of dine-in information added by the restaurant, e.g. `table` or `booth`.'
                        )
                        .optional(),
                      value: z
                        .string()
                        .describe(
                          'Value of dine-in information added by the restaurant, e.g. `#6`.'
                        )
                        .optional(),
                    })
                    .describe('Dine-in details'),
                }),
              ])
            )
            .describe('Order fulfillment information.')
            .optional(),
          customer: z
            .intersection(
              z.object({
                firstName: z.string().describe('First name.').optional(),
                lastName: z.string().describe('Last name.').optional(),
                phone: z
                  .string()
                  .describe('Phone number.')
                  .optional()
                  .nullable(),
                email: z.string().describe('Email address.').optional(),
                contactId: z
                  .string()
                  .describe(
                    "Customer's contact ID.\nSee [Contacts API](https://dev.wix.com/api/rest/contacts/contacts/contacts-v4) for more details."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({
                  memberId: z.never().optional(),
                  visitorId: z.never().optional(),
                }),
                z.object({
                  visitorId: z.never().optional(),
                  memberId: z
                    .string()
                    .describe('Member ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
                z.object({
                  memberId: z.never().optional(),
                  visitorId: z
                    .string()
                    .describe('Visitor ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                }),
              ])
            )
            .describe('Customer information.')
            .optional(),
          totals: z
            .object({
              subtotal: z
                .string()
                .describe(
                  'Order subtotal. Calculated by substracting delivery fee, tax and discount from order total.'
                )
                .optional(),
              total: z.string().describe('Total order price.').optional(),
              delivery: z
                .string()
                .describe('Total delivery fees charged to the customer.')
                .optional()
                .nullable(),
              tax: z.string().describe('Total tax.').optional().nullable(),
              discount: z
                .string()
                .describe('Total discount amount.')
                .optional()
                .nullable(),
              loyaltySavings: z
                .string()
                .describe('Total amount saved through the Wix Loyalty program.')
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe('Total number of line items.')
                .min(0)
                .optional(),
              tip: z.string().describe('Total tip.').optional().nullable(),
            })
            .describe('Order totals.')
            .optional(),
          activities: z
            .array(
              z.object({
                timestamp: z
                  .date()
                  .describe('Activity timestamp.')
                  .optional()
                  .nullable(),
                message: z
                  .string()
                  .describe('Optional message added during order activity.')
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .optional(),
          channelInfo: z
            .object({
              type: z
                .enum([
                  'UNSPECIFIED_CHANNEL_TYPE',
                  'WEB',
                  'MOBILE_WEB',
                  'MOBILE',
                  'CALL_CENTER',
                  'FACEBOOK',
                  'TPA',
                ])
                .optional(),
            })
            .describe(
              'Information about the sales channel that submitted the order.'
            )
            .optional(),
          loyaltyInfo: z
            .object({
              rewardId: z
                .string()
                .describe('Associated Wix Loyalty reward ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              appliedAmount: z
                .string()
                .describe('Amount saved redeeming Wix Loyalty reward.')
                .optional()
                .nullable(),
              redeemedPoints: z
                .number()
                .int()
                .describe('Wix Loyalty points redeemed.')
                .min(0)
                .optional()
                .nullable(),
              transactionId: z
                .string()
                .describe('Associated Wix Loyalty transaction ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              estimatedAccountBalance: z
                .number()
                .int()
                .describe(
                  'Wix Loyalty estimated account point balance.\nEqual to the sum of earned and adjusted points minus redeemed points.\nThe estimated point balance is the value before the order is completed. Following the purchase, the point balance is updated.'
                )
                .min(0)
                .optional()
                .nullable(),
              estimatedPointsEarned: z
                .number()
                .int()
                .describe(
                  'Wix Loyalty estimated total earned points.\nThe estimated total earned points is the value before the order is completed. Following the purchase, the total is updated.'
                )
                .min(0)
                .optional()
                .nullable(),
              rewardRevision: z
                .string()
                .describe(
                  'Associated Wix Loyalty reward revision number, which increments by 1 each time the loyalty account is updated.\n\nTo prevent conflicting changes, the current revision must be passed when updating the loyalty account.\nIgnored when creating an account.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Information about the order’s loyalty points.\nLearn more about the [Wix Loyalty Program](https://dev.wix.com/api/rest/wix-loyalty-program/introduction).'
            )
            .optional(),
        })
      )
      .optional(),
  });
})();
export const AcceptOrderRequest = z.object({
  _id: z.string().describe('ID of the order to mark as `ACCEPTED`.'),
  options: z
    .object({
      message: z
        .string()
        .describe('Message when accepting the order.')
        .max(1000)
        .optional()
        .nullable(),
    })
    .optional(),
});
export const AcceptOrderResponse = (() => {
  let comWixpressRestaurantsOrdersV3LineItemSchema: z.ZodType<any> = z.object({
    quantity: z
      .number()
      .int()
      .describe('Line item quantity.')
      .min(1)
      .max(100000)
      .optional(),
    price: z.string().describe('Line item price.').optional(),
    comment: z
      .string()
      .describe('Comment about the line item added by the customer.')
      .max(1000)
      .optional()
      .nullable(),
    dishOptions: z
      .array(
        z.object({
          name: z
            .string()
            .describe('Line item option name.')
            .max(1000)
            .optional()
            .nullable(),
          selectedChoices: z
            .array(z.lazy(() => comWixpressRestaurantsOrdersV3LineItemSchema))
            .min(0)
            .max(300)
            .optional(),
          minChoices: z
            .number()
            .int()
            .describe(
              'Minimum number of dish options the customer is required to choose.'
            )
            .min(0)
            .max(2147483647)
            .optional()
            .nullable(),
          maxChoices: z
            .number()
            .int()
            .describe(
              'Maximum number of dish options the customer is allowed to choose.'
            )
            .min(1)
            .max(2147483647)
            .optional()
            .nullable(),
          type: z
            .enum([
              'UNSPECIFIED_DISPLAY_TYPE',
              'SELECTION',
              'EXTRAS',
              'DESELECTION',
            ])
            .describe('Dish option type.')
            .optional(),
          availableChoices: z
            .array(
              z.object({
                itemId: z
                  .string()
                  .describe('Line item ID as defined in the catalog.')
                  .optional()
                  .nullable(),
                price: z
                  .string()
                  .describe('Line item price.')
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Line item name.')
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .min(1)
            .max(300)
            .optional(),
          defaultChoices: z.array(z.string()).min(0).max(300).optional(),
        })
      )
      .min(0)
      .max(300)
      .optional(),
    catalogReference: z
      .object({
        catalogItemId: z
          .string()
          .describe('Line item ID as defined in the catalog.')
          .optional(),
        catalogItemName: z
          .string()
          .describe('Line item name as defined in the catalog.')
          .max(1000)
          .optional()
          .nullable(),
        catalogItemDescription: z
          .string()
          .describe('Item description as defined in the catalog.')
          .max(1000)
          .optional()
          .nullable(),
        catalogItemMedia: z
          .string()
          .describe('Item media url as defined in the catalog.')
          .url()
          .optional()
          .nullable(),
      })
      .describe('References to the line item’s origin catalog.')
      .optional(),
  });
  return z.object({
    order: z
      .object({
        _id: z.string().describe('Order ID.').optional(),
        locationId: z
          .string()
          .describe('ID of the restaurant’s location.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Order creation date and time in `yyyy-mm-ddThh:mm:sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            "Date and time of order's latest update in `yyyy-mm-ddThh:mm:sssZ` format."
          )
          .optional()
          .nullable(),
        comment: z
          .string()
          .describe('Additional note to the order added by the customer.')
          .max(1000)
          .optional()
          .nullable(),
        currency: z
          .string()
          .describe('Currency of the order.')
          .optional()
          .nullable(),
        status: z
          .enum([
            'UNSPECIFIED_ORDER_STATUS',
            'PENDING',
            'NEW',
            'ACCEPTED',
            'CANCELED',
            'FULFILLED',
          ])
          .describe('Current order status.')
          .optional(),
        lineItems: z
          .array(comWixpressRestaurantsOrdersV3LineItemSchema)
          .min(1)
          .max(300)
          .optional(),
        discounts: z
          .array(
            z.object({
              catalogDiscountId: z
                .string()
                .describe('Discount ID as defined in the catalog.')
                .optional(),
              appliedAmount: z.string().describe('Amount saved.').optional(),
              catalogDiscountType: z
                .enum([
                  'UNSPECIFIED_TYPE',
                  'OFF_ITEM',
                  'OFF_ORDER',
                  'OFF_ORDER_MANAGER_DISCOUNT',
                ])
                .describe('Discount type.')
                .optional(),
              catalogDiscountName: z
                .string()
                .describe('Discount name as defined in the catalog.')
                .min(1)
                .max(256)
                .optional(),
              catalogDiscountDescription: z
                .string()
                .describe(
                  'Discount description as defined in the catalog.\n\nMax: 1,000 characters'
                )
                .min(1)
                .max(1000)
                .optional(),
            })
          )
          .min(0)
          .max(100)
          .optional(),
        coupon: z
          .object({
            code: z.string().describe('Coupon code.').optional(),
            _id: z
              .string()
              .describe('Coupon ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
          })
          .describe('Coupon applied to the order.')
          .optional(),
        payments: z
          .array(
            z.object({
              type: z
                .enum([
                  'UNSPECIFIED_PAYMENT_TYPE',
                  'CASH',
                  'CREDIT',
                  'DELIVERY_CLUB',
                  'DELIVERY_COM',
                  'BITPAY',
                  'CELLARIX',
                  'BITSOFGOLD',
                  'MULTIPASS',
                  'TENBIS',
                  'PAYPAL',
                  'MYSODEXO',
                  'WIX_PAYMENTS',
                ])
                .describe('Payment type.')
                .optional(),
              amount: z
                .string()
                .describe(
                  'Amount paid using this payment type. Only differs from total amount paid in case of split payments.'
                )
                .optional()
                .nullable(),
              method: z
                .string()
                .describe('Payment method.')
                .optional()
                .nullable(),
              providerTransactionId: z
                .string()
                .describe(
                  'Transaction ID.\nSee [Cashier API](https://dev.wix.com/api/rest/wix-cashier/payments/transaction) for more details.'
                )
                .optional()
                .nullable(),
            })
          )
          .min(1)
          .max(100)
          .optional(),
        fulfillment: z
          .intersection(
            z.object({
              type: z
                .enum([
                  'UNSPECIFIED_FULFILLMENT_TYPE',
                  'PICKUP',
                  'DELIVERY',
                  'DINE_IN',
                ])
                .describe('Fulfillment type.')
                .optional(),
              promisedTime: z
                .date()
                .describe('Latest delivery time promised by the restaurant.')
                .optional()
                .nullable(),
              asap: z
                .boolean()
                .describe(
                  'Whether the order should be fulfilled as soon as possible. Defaults to `true`.'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                deliveryDetails: z.never().optional(),
                pickupDetails: z.never().optional(),
                dineInDetails: z.never().optional(),
              }),
              z.object({
                pickupDetails: z.never().optional(),
                dineInDetails: z.never().optional(),
                deliveryDetails: z
                  .intersection(
                    z.object({
                      address: z
                        .object({
                          formatted: z
                            .string()
                            .describe('Formatted delivery address.')
                            .max(250)
                            .optional()
                            .nullable(),
                          country: z
                            .string()
                            .describe('Country.')
                            .max(100)
                            .optional()
                            .nullable(),
                          city: z
                            .string()
                            .describe('City name.')
                            .max(100)
                            .optional()
                            .nullable(),
                          street: z
                            .string()
                            .describe('Street name.')
                            .max(100)
                            .optional()
                            .nullable(),
                          streetNumber: z
                            .string()
                            .describe('Street number.')
                            .max(100)
                            .optional()
                            .nullable(),
                          apt: z
                            .string()
                            .describe('Apartment number.')
                            .max(100)
                            .optional()
                            .nullable(),
                          floor: z
                            .string()
                            .describe('Floor.')
                            .max(100)
                            .optional()
                            .nullable(),
                          entrance: z
                            .string()
                            .describe('Entrance.')
                            .max(100)
                            .optional()
                            .nullable(),
                          zipCode: z
                            .string()
                            .describe('ZIP/postal code.')
                            .max(100)
                            .optional()
                            .nullable(),
                          countryCode: z
                            .string()
                            .describe(
                              'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                            )
                            .optional()
                            .nullable(),
                          onArrival: z
                            .enum([
                              'UNSPECIFIED_ON_ARRIVAL_TYPE',
                              'BUZZ_DOOR',
                              'CALL_ME',
                            ])
                            .describe(
                              'Special delivery instructions upon arrival to the address.'
                            )
                            .optional(),
                          approximate: z
                            .boolean()
                            .describe(
                              'Whether an approximate address is used. Defaults to `false`. In case of `false` a house number is required.'
                            )
                            .optional()
                            .nullable(),
                          comment: z
                            .string()
                            .describe(
                              'Delivery Instructions added by the customer.'
                            )
                            .max(500)
                            .optional()
                            .nullable(),
                          location: z
                            .object({
                              latitude: z
                                .number()
                                .describe('Address latitude.')
                                .optional()
                                .nullable(),
                              longitude: z
                                .number()
                                .describe('Address longitude.')
                                .optional()
                                .nullable(),
                            })
                            .describe('Geo coordinates of the address.')
                            .optional(),
                          addressLine2: z
                            .string()
                            .describe('Address Line 2.')
                            .optional()
                            .nullable(),
                          subdivision: z
                            .string()
                            .describe(
                              'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe('Delivery address.')
                        .optional(),
                      pickupInfo: z
                        .object({
                          windowStartTime: z
                            .date()
                            .describe(
                              'When a delivery is ready to be picked up. This is the start time of the pickup window.'
                            )
                            .optional()
                            .nullable(),
                          windowEndTime: z
                            .date()
                            .describe(
                              'When a delivery must be picked up by. This is the end time of the pickup window.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe('Information about the delivery pickup.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        restaurant: z.never().optional(),
                        externalProvider: z.never().optional(),
                      }),
                      z.object({
                        externalProvider: z.never().optional(),
                        restaurant: z
                          .object({
                            fee: z
                              .string()
                              .describe('Delivery fee.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Delivery through the restaurant.'),
                      }),
                      z.object({
                        restaurant: z.never().optional(),
                        externalProvider: z
                          .object({
                            _id: z
                              .string()
                              .describe('ID of the external delivery provider.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .string()
                              .describe(
                                'Name of the external delivery provider.'
                              )
                              .optional(),
                            customerFee: z
                              .string()
                              .describe('Delivery fee charged to the customer.')
                              .optional()
                              .nullable(),
                            commission: z
                              .string()
                              .describe(
                                'Fee paid by the restaurant to the external delivery provider.'
                              )
                              .optional()
                              .nullable(),
                            pickupTime: z
                              .date()
                              .describe('Order pickup time.')
                              .optional()
                              .nullable(),
                            dropOffTime: z
                              .date()
                              .describe('Order drop off time.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Delivery through an external provider.'),
                      }),
                    ])
                  )
                  .describe('Delivery details.'),
              }),
              z.object({
                deliveryDetails: z.never().optional(),
                dineInDetails: z.never().optional(),
                pickupDetails: z
                  .object({
                    fee: z
                      .string()
                      .describe('Pickup fee charged to the customer.')
                      .optional()
                      .nullable(),
                    curbside: z
                      .object({
                        info: z
                          .string()
                          .describe(
                            'Additional information for curbside pickup.'
                          )
                          .min(0)
                          .max(50)
                          .optional(),
                      })
                      .describe('Curbside pickup method')
                      .optional(),
                  })
                  .describe('Pickup details.'),
              }),
              z.object({
                deliveryDetails: z.never().optional(),
                pickupDetails: z.never().optional(),
                dineInDetails: z
                  .object({
                    label: z
                      .string()
                      .describe(
                        'Label of dine-in information added by the restaurant, e.g. `table` or `booth`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Value of dine-in information added by the restaurant, e.g. `#6`.'
                      )
                      .optional(),
                  })
                  .describe('Dine-in details'),
              }),
            ])
          )
          .describe('Order fulfillment information.')
          .optional(),
        customer: z
          .intersection(
            z.object({
              firstName: z.string().describe('First name.').optional(),
              lastName: z.string().describe('Last name.').optional(),
              phone: z.string().describe('Phone number.').optional().nullable(),
              email: z.string().describe('Email address.').optional(),
              contactId: z
                .string()
                .describe(
                  "Customer's contact ID.\nSee [Contacts API](https://dev.wix.com/api/rest/contacts/contacts/contacts-v4) for more details."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                memberId: z.never().optional(),
                visitorId: z.never().optional(),
              }),
              z.object({
                visitorId: z.never().optional(),
                memberId: z
                  .string()
                  .describe('Member ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                memberId: z.never().optional(),
                visitorId: z
                  .string()
                  .describe('Visitor ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe('Customer information.')
          .optional(),
        totals: z
          .object({
            subtotal: z
              .string()
              .describe(
                'Order subtotal. Calculated by substracting delivery fee, tax and discount from order total.'
              )
              .optional(),
            total: z.string().describe('Total order price.').optional(),
            delivery: z
              .string()
              .describe('Total delivery fees charged to the customer.')
              .optional()
              .nullable(),
            tax: z.string().describe('Total tax.').optional().nullable(),
            discount: z
              .string()
              .describe('Total discount amount.')
              .optional()
              .nullable(),
            loyaltySavings: z
              .string()
              .describe('Total amount saved through the Wix Loyalty program.')
              .optional()
              .nullable(),
            quantity: z
              .number()
              .int()
              .describe('Total number of line items.')
              .min(0)
              .optional(),
            tip: z.string().describe('Total tip.').optional().nullable(),
          })
          .describe('Order totals.')
          .optional(),
        activities: z
          .array(
            z.object({
              timestamp: z
                .date()
                .describe('Activity timestamp.')
                .optional()
                .nullable(),
              message: z
                .string()
                .describe('Optional message added during order activity.')
                .max(1000)
                .optional()
                .nullable(),
            })
          )
          .optional(),
        channelInfo: z
          .object({
            type: z
              .enum([
                'UNSPECIFIED_CHANNEL_TYPE',
                'WEB',
                'MOBILE_WEB',
                'MOBILE',
                'CALL_CENTER',
                'FACEBOOK',
                'TPA',
              ])
              .optional(),
          })
          .describe(
            'Information about the sales channel that submitted the order.'
          )
          .optional(),
        loyaltyInfo: z
          .object({
            rewardId: z
              .string()
              .describe('Associated Wix Loyalty reward ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            appliedAmount: z
              .string()
              .describe('Amount saved redeeming Wix Loyalty reward.')
              .optional()
              .nullable(),
            redeemedPoints: z
              .number()
              .int()
              .describe('Wix Loyalty points redeemed.')
              .min(0)
              .optional()
              .nullable(),
            transactionId: z
              .string()
              .describe('Associated Wix Loyalty transaction ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            estimatedAccountBalance: z
              .number()
              .int()
              .describe(
                'Wix Loyalty estimated account point balance.\nEqual to the sum of earned and adjusted points minus redeemed points.\nThe estimated point balance is the value before the order is completed. Following the purchase, the point balance is updated.'
              )
              .min(0)
              .optional()
              .nullable(),
            estimatedPointsEarned: z
              .number()
              .int()
              .describe(
                'Wix Loyalty estimated total earned points.\nThe estimated total earned points is the value before the order is completed. Following the purchase, the total is updated.'
              )
              .min(0)
              .optional()
              .nullable(),
            rewardRevision: z
              .string()
              .describe(
                'Associated Wix Loyalty reward revision number, which increments by 1 each time the loyalty account is updated.\n\nTo prevent conflicting changes, the current revision must be passed when updating the loyalty account.\nIgnored when creating an account.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Information about the order’s loyalty points.\nLearn more about the [Wix Loyalty Program](https://dev.wix.com/api/rest/wix-loyalty-program/introduction).'
          )
          .optional(),
      })
      .describe('Accepted order.')
      .optional(),
  });
})();
export const CancelOrderRequest = z.object({
  _id: z.string().describe('ID of the order to mark as `CANCELED`.'),
  options: z
    .object({
      message: z
        .string()
        .describe('Message when canceling the order.')
        .max(1000)
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CancelOrderResponse = (() => {
  let comWixpressRestaurantsOrdersV3LineItemSchema: z.ZodType<any> = z.object({
    quantity: z
      .number()
      .int()
      .describe('Line item quantity.')
      .min(1)
      .max(100000)
      .optional(),
    price: z.string().describe('Line item price.').optional(),
    comment: z
      .string()
      .describe('Comment about the line item added by the customer.')
      .max(1000)
      .optional()
      .nullable(),
    dishOptions: z
      .array(
        z.object({
          name: z
            .string()
            .describe('Line item option name.')
            .max(1000)
            .optional()
            .nullable(),
          selectedChoices: z
            .array(z.lazy(() => comWixpressRestaurantsOrdersV3LineItemSchema))
            .min(0)
            .max(300)
            .optional(),
          minChoices: z
            .number()
            .int()
            .describe(
              'Minimum number of dish options the customer is required to choose.'
            )
            .min(0)
            .max(2147483647)
            .optional()
            .nullable(),
          maxChoices: z
            .number()
            .int()
            .describe(
              'Maximum number of dish options the customer is allowed to choose.'
            )
            .min(1)
            .max(2147483647)
            .optional()
            .nullable(),
          type: z
            .enum([
              'UNSPECIFIED_DISPLAY_TYPE',
              'SELECTION',
              'EXTRAS',
              'DESELECTION',
            ])
            .describe('Dish option type.')
            .optional(),
          availableChoices: z
            .array(
              z.object({
                itemId: z
                  .string()
                  .describe('Line item ID as defined in the catalog.')
                  .optional()
                  .nullable(),
                price: z
                  .string()
                  .describe('Line item price.')
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Line item name.')
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .min(1)
            .max(300)
            .optional(),
          defaultChoices: z.array(z.string()).min(0).max(300).optional(),
        })
      )
      .min(0)
      .max(300)
      .optional(),
    catalogReference: z
      .object({
        catalogItemId: z
          .string()
          .describe('Line item ID as defined in the catalog.')
          .optional(),
        catalogItemName: z
          .string()
          .describe('Line item name as defined in the catalog.')
          .max(1000)
          .optional()
          .nullable(),
        catalogItemDescription: z
          .string()
          .describe('Item description as defined in the catalog.')
          .max(1000)
          .optional()
          .nullable(),
        catalogItemMedia: z
          .string()
          .describe('Item media url as defined in the catalog.')
          .url()
          .optional()
          .nullable(),
      })
      .describe('References to the line item’s origin catalog.')
      .optional(),
  });
  return z.object({
    order: z
      .object({
        _id: z.string().describe('Order ID.').optional(),
        locationId: z
          .string()
          .describe('ID of the restaurant’s location.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Order creation date and time in `yyyy-mm-ddThh:mm:sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            "Date and time of order's latest update in `yyyy-mm-ddThh:mm:sssZ` format."
          )
          .optional()
          .nullable(),
        comment: z
          .string()
          .describe('Additional note to the order added by the customer.')
          .max(1000)
          .optional()
          .nullable(),
        currency: z
          .string()
          .describe('Currency of the order.')
          .optional()
          .nullable(),
        status: z
          .enum([
            'UNSPECIFIED_ORDER_STATUS',
            'PENDING',
            'NEW',
            'ACCEPTED',
            'CANCELED',
            'FULFILLED',
          ])
          .describe('Current order status.')
          .optional(),
        lineItems: z
          .array(comWixpressRestaurantsOrdersV3LineItemSchema)
          .min(1)
          .max(300)
          .optional(),
        discounts: z
          .array(
            z.object({
              catalogDiscountId: z
                .string()
                .describe('Discount ID as defined in the catalog.')
                .optional(),
              appliedAmount: z.string().describe('Amount saved.').optional(),
              catalogDiscountType: z
                .enum([
                  'UNSPECIFIED_TYPE',
                  'OFF_ITEM',
                  'OFF_ORDER',
                  'OFF_ORDER_MANAGER_DISCOUNT',
                ])
                .describe('Discount type.')
                .optional(),
              catalogDiscountName: z
                .string()
                .describe('Discount name as defined in the catalog.')
                .min(1)
                .max(256)
                .optional(),
              catalogDiscountDescription: z
                .string()
                .describe(
                  'Discount description as defined in the catalog.\n\nMax: 1,000 characters'
                )
                .min(1)
                .max(1000)
                .optional(),
            })
          )
          .min(0)
          .max(100)
          .optional(),
        coupon: z
          .object({
            code: z.string().describe('Coupon code.').optional(),
            _id: z
              .string()
              .describe('Coupon ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
          })
          .describe('Coupon applied to the order.')
          .optional(),
        payments: z
          .array(
            z.object({
              type: z
                .enum([
                  'UNSPECIFIED_PAYMENT_TYPE',
                  'CASH',
                  'CREDIT',
                  'DELIVERY_CLUB',
                  'DELIVERY_COM',
                  'BITPAY',
                  'CELLARIX',
                  'BITSOFGOLD',
                  'MULTIPASS',
                  'TENBIS',
                  'PAYPAL',
                  'MYSODEXO',
                  'WIX_PAYMENTS',
                ])
                .describe('Payment type.')
                .optional(),
              amount: z
                .string()
                .describe(
                  'Amount paid using this payment type. Only differs from total amount paid in case of split payments.'
                )
                .optional()
                .nullable(),
              method: z
                .string()
                .describe('Payment method.')
                .optional()
                .nullable(),
              providerTransactionId: z
                .string()
                .describe(
                  'Transaction ID.\nSee [Cashier API](https://dev.wix.com/api/rest/wix-cashier/payments/transaction) for more details.'
                )
                .optional()
                .nullable(),
            })
          )
          .min(1)
          .max(100)
          .optional(),
        fulfillment: z
          .intersection(
            z.object({
              type: z
                .enum([
                  'UNSPECIFIED_FULFILLMENT_TYPE',
                  'PICKUP',
                  'DELIVERY',
                  'DINE_IN',
                ])
                .describe('Fulfillment type.')
                .optional(),
              promisedTime: z
                .date()
                .describe('Latest delivery time promised by the restaurant.')
                .optional()
                .nullable(),
              asap: z
                .boolean()
                .describe(
                  'Whether the order should be fulfilled as soon as possible. Defaults to `true`.'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                deliveryDetails: z.never().optional(),
                pickupDetails: z.never().optional(),
                dineInDetails: z.never().optional(),
              }),
              z.object({
                pickupDetails: z.never().optional(),
                dineInDetails: z.never().optional(),
                deliveryDetails: z
                  .intersection(
                    z.object({
                      address: z
                        .object({
                          formatted: z
                            .string()
                            .describe('Formatted delivery address.')
                            .max(250)
                            .optional()
                            .nullable(),
                          country: z
                            .string()
                            .describe('Country.')
                            .max(100)
                            .optional()
                            .nullable(),
                          city: z
                            .string()
                            .describe('City name.')
                            .max(100)
                            .optional()
                            .nullable(),
                          street: z
                            .string()
                            .describe('Street name.')
                            .max(100)
                            .optional()
                            .nullable(),
                          streetNumber: z
                            .string()
                            .describe('Street number.')
                            .max(100)
                            .optional()
                            .nullable(),
                          apt: z
                            .string()
                            .describe('Apartment number.')
                            .max(100)
                            .optional()
                            .nullable(),
                          floor: z
                            .string()
                            .describe('Floor.')
                            .max(100)
                            .optional()
                            .nullable(),
                          entrance: z
                            .string()
                            .describe('Entrance.')
                            .max(100)
                            .optional()
                            .nullable(),
                          zipCode: z
                            .string()
                            .describe('ZIP/postal code.')
                            .max(100)
                            .optional()
                            .nullable(),
                          countryCode: z
                            .string()
                            .describe(
                              'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                            )
                            .optional()
                            .nullable(),
                          onArrival: z
                            .enum([
                              'UNSPECIFIED_ON_ARRIVAL_TYPE',
                              'BUZZ_DOOR',
                              'CALL_ME',
                            ])
                            .describe(
                              'Special delivery instructions upon arrival to the address.'
                            )
                            .optional(),
                          approximate: z
                            .boolean()
                            .describe(
                              'Whether an approximate address is used. Defaults to `false`. In case of `false` a house number is required.'
                            )
                            .optional()
                            .nullable(),
                          comment: z
                            .string()
                            .describe(
                              'Delivery Instructions added by the customer.'
                            )
                            .max(500)
                            .optional()
                            .nullable(),
                          location: z
                            .object({
                              latitude: z
                                .number()
                                .describe('Address latitude.')
                                .optional()
                                .nullable(),
                              longitude: z
                                .number()
                                .describe('Address longitude.')
                                .optional()
                                .nullable(),
                            })
                            .describe('Geo coordinates of the address.')
                            .optional(),
                          addressLine2: z
                            .string()
                            .describe('Address Line 2.')
                            .optional()
                            .nullable(),
                          subdivision: z
                            .string()
                            .describe(
                              'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe('Delivery address.')
                        .optional(),
                      pickupInfo: z
                        .object({
                          windowStartTime: z
                            .date()
                            .describe(
                              'When a delivery is ready to be picked up. This is the start time of the pickup window.'
                            )
                            .optional()
                            .nullable(),
                          windowEndTime: z
                            .date()
                            .describe(
                              'When a delivery must be picked up by. This is the end time of the pickup window.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe('Information about the delivery pickup.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        restaurant: z.never().optional(),
                        externalProvider: z.never().optional(),
                      }),
                      z.object({
                        externalProvider: z.never().optional(),
                        restaurant: z
                          .object({
                            fee: z
                              .string()
                              .describe('Delivery fee.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Delivery through the restaurant.'),
                      }),
                      z.object({
                        restaurant: z.never().optional(),
                        externalProvider: z
                          .object({
                            _id: z
                              .string()
                              .describe('ID of the external delivery provider.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .string()
                              .describe(
                                'Name of the external delivery provider.'
                              )
                              .optional(),
                            customerFee: z
                              .string()
                              .describe('Delivery fee charged to the customer.')
                              .optional()
                              .nullable(),
                            commission: z
                              .string()
                              .describe(
                                'Fee paid by the restaurant to the external delivery provider.'
                              )
                              .optional()
                              .nullable(),
                            pickupTime: z
                              .date()
                              .describe('Order pickup time.')
                              .optional()
                              .nullable(),
                            dropOffTime: z
                              .date()
                              .describe('Order drop off time.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Delivery through an external provider.'),
                      }),
                    ])
                  )
                  .describe('Delivery details.'),
              }),
              z.object({
                deliveryDetails: z.never().optional(),
                dineInDetails: z.never().optional(),
                pickupDetails: z
                  .object({
                    fee: z
                      .string()
                      .describe('Pickup fee charged to the customer.')
                      .optional()
                      .nullable(),
                    curbside: z
                      .object({
                        info: z
                          .string()
                          .describe(
                            'Additional information for curbside pickup.'
                          )
                          .min(0)
                          .max(50)
                          .optional(),
                      })
                      .describe('Curbside pickup method')
                      .optional(),
                  })
                  .describe('Pickup details.'),
              }),
              z.object({
                deliveryDetails: z.never().optional(),
                pickupDetails: z.never().optional(),
                dineInDetails: z
                  .object({
                    label: z
                      .string()
                      .describe(
                        'Label of dine-in information added by the restaurant, e.g. `table` or `booth`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Value of dine-in information added by the restaurant, e.g. `#6`.'
                      )
                      .optional(),
                  })
                  .describe('Dine-in details'),
              }),
            ])
          )
          .describe('Order fulfillment information.')
          .optional(),
        customer: z
          .intersection(
            z.object({
              firstName: z.string().describe('First name.').optional(),
              lastName: z.string().describe('Last name.').optional(),
              phone: z.string().describe('Phone number.').optional().nullable(),
              email: z.string().describe('Email address.').optional(),
              contactId: z
                .string()
                .describe(
                  "Customer's contact ID.\nSee [Contacts API](https://dev.wix.com/api/rest/contacts/contacts/contacts-v4) for more details."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                memberId: z.never().optional(),
                visitorId: z.never().optional(),
              }),
              z.object({
                visitorId: z.never().optional(),
                memberId: z
                  .string()
                  .describe('Member ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                memberId: z.never().optional(),
                visitorId: z
                  .string()
                  .describe('Visitor ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe('Customer information.')
          .optional(),
        totals: z
          .object({
            subtotal: z
              .string()
              .describe(
                'Order subtotal. Calculated by substracting delivery fee, tax and discount from order total.'
              )
              .optional(),
            total: z.string().describe('Total order price.').optional(),
            delivery: z
              .string()
              .describe('Total delivery fees charged to the customer.')
              .optional()
              .nullable(),
            tax: z.string().describe('Total tax.').optional().nullable(),
            discount: z
              .string()
              .describe('Total discount amount.')
              .optional()
              .nullable(),
            loyaltySavings: z
              .string()
              .describe('Total amount saved through the Wix Loyalty program.')
              .optional()
              .nullable(),
            quantity: z
              .number()
              .int()
              .describe('Total number of line items.')
              .min(0)
              .optional(),
            tip: z.string().describe('Total tip.').optional().nullable(),
          })
          .describe('Order totals.')
          .optional(),
        activities: z
          .array(
            z.object({
              timestamp: z
                .date()
                .describe('Activity timestamp.')
                .optional()
                .nullable(),
              message: z
                .string()
                .describe('Optional message added during order activity.')
                .max(1000)
                .optional()
                .nullable(),
            })
          )
          .optional(),
        channelInfo: z
          .object({
            type: z
              .enum([
                'UNSPECIFIED_CHANNEL_TYPE',
                'WEB',
                'MOBILE_WEB',
                'MOBILE',
                'CALL_CENTER',
                'FACEBOOK',
                'TPA',
              ])
              .optional(),
          })
          .describe(
            'Information about the sales channel that submitted the order.'
          )
          .optional(),
        loyaltyInfo: z
          .object({
            rewardId: z
              .string()
              .describe('Associated Wix Loyalty reward ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            appliedAmount: z
              .string()
              .describe('Amount saved redeeming Wix Loyalty reward.')
              .optional()
              .nullable(),
            redeemedPoints: z
              .number()
              .int()
              .describe('Wix Loyalty points redeemed.')
              .min(0)
              .optional()
              .nullable(),
            transactionId: z
              .string()
              .describe('Associated Wix Loyalty transaction ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            estimatedAccountBalance: z
              .number()
              .int()
              .describe(
                'Wix Loyalty estimated account point balance.\nEqual to the sum of earned and adjusted points minus redeemed points.\nThe estimated point balance is the value before the order is completed. Following the purchase, the point balance is updated.'
              )
              .min(0)
              .optional()
              .nullable(),
            estimatedPointsEarned: z
              .number()
              .int()
              .describe(
                'Wix Loyalty estimated total earned points.\nThe estimated total earned points is the value before the order is completed. Following the purchase, the total is updated.'
              )
              .min(0)
              .optional()
              .nullable(),
            rewardRevision: z
              .string()
              .describe(
                'Associated Wix Loyalty reward revision number, which increments by 1 each time the loyalty account is updated.\n\nTo prevent conflicting changes, the current revision must be passed when updating the loyalty account.\nIgnored when creating an account.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Information about the order’s loyalty points.\nLearn more about the [Wix Loyalty Program](https://dev.wix.com/api/rest/wix-loyalty-program/introduction).'
          )
          .optional(),
      })
      .describe('Canceled order.')
      .optional(),
  });
})();
export const FulfillOrderRequest = z.object({
  _id: z.string().describe('ID of the order to mark as `FULFILLED`.'),
});
export const FulfillOrderResponse = (() => {
  let comWixpressRestaurantsOrdersV3LineItemSchema: z.ZodType<any> = z.object({
    quantity: z
      .number()
      .int()
      .describe('Line item quantity.')
      .min(1)
      .max(100000)
      .optional(),
    price: z.string().describe('Line item price.').optional(),
    comment: z
      .string()
      .describe('Comment about the line item added by the customer.')
      .max(1000)
      .optional()
      .nullable(),
    dishOptions: z
      .array(
        z.object({
          name: z
            .string()
            .describe('Line item option name.')
            .max(1000)
            .optional()
            .nullable(),
          selectedChoices: z
            .array(z.lazy(() => comWixpressRestaurantsOrdersV3LineItemSchema))
            .min(0)
            .max(300)
            .optional(),
          minChoices: z
            .number()
            .int()
            .describe(
              'Minimum number of dish options the customer is required to choose.'
            )
            .min(0)
            .max(2147483647)
            .optional()
            .nullable(),
          maxChoices: z
            .number()
            .int()
            .describe(
              'Maximum number of dish options the customer is allowed to choose.'
            )
            .min(1)
            .max(2147483647)
            .optional()
            .nullable(),
          type: z
            .enum([
              'UNSPECIFIED_DISPLAY_TYPE',
              'SELECTION',
              'EXTRAS',
              'DESELECTION',
            ])
            .describe('Dish option type.')
            .optional(),
          availableChoices: z
            .array(
              z.object({
                itemId: z
                  .string()
                  .describe('Line item ID as defined in the catalog.')
                  .optional()
                  .nullable(),
                price: z
                  .string()
                  .describe('Line item price.')
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Line item name.')
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .min(1)
            .max(300)
            .optional(),
          defaultChoices: z.array(z.string()).min(0).max(300).optional(),
        })
      )
      .min(0)
      .max(300)
      .optional(),
    catalogReference: z
      .object({
        catalogItemId: z
          .string()
          .describe('Line item ID as defined in the catalog.')
          .optional(),
        catalogItemName: z
          .string()
          .describe('Line item name as defined in the catalog.')
          .max(1000)
          .optional()
          .nullable(),
        catalogItemDescription: z
          .string()
          .describe('Item description as defined in the catalog.')
          .max(1000)
          .optional()
          .nullable(),
        catalogItemMedia: z
          .string()
          .describe('Item media url as defined in the catalog.')
          .url()
          .optional()
          .nullable(),
      })
      .describe('References to the line item’s origin catalog.')
      .optional(),
  });
  return z.object({
    order: z
      .object({
        _id: z.string().describe('Order ID.').optional(),
        locationId: z
          .string()
          .describe('ID of the restaurant’s location.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Order creation date and time in `yyyy-mm-ddThh:mm:sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            "Date and time of order's latest update in `yyyy-mm-ddThh:mm:sssZ` format."
          )
          .optional()
          .nullable(),
        comment: z
          .string()
          .describe('Additional note to the order added by the customer.')
          .max(1000)
          .optional()
          .nullable(),
        currency: z
          .string()
          .describe('Currency of the order.')
          .optional()
          .nullable(),
        status: z
          .enum([
            'UNSPECIFIED_ORDER_STATUS',
            'PENDING',
            'NEW',
            'ACCEPTED',
            'CANCELED',
            'FULFILLED',
          ])
          .describe('Current order status.')
          .optional(),
        lineItems: z
          .array(comWixpressRestaurantsOrdersV3LineItemSchema)
          .min(1)
          .max(300)
          .optional(),
        discounts: z
          .array(
            z.object({
              catalogDiscountId: z
                .string()
                .describe('Discount ID as defined in the catalog.')
                .optional(),
              appliedAmount: z.string().describe('Amount saved.').optional(),
              catalogDiscountType: z
                .enum([
                  'UNSPECIFIED_TYPE',
                  'OFF_ITEM',
                  'OFF_ORDER',
                  'OFF_ORDER_MANAGER_DISCOUNT',
                ])
                .describe('Discount type.')
                .optional(),
              catalogDiscountName: z
                .string()
                .describe('Discount name as defined in the catalog.')
                .min(1)
                .max(256)
                .optional(),
              catalogDiscountDescription: z
                .string()
                .describe(
                  'Discount description as defined in the catalog.\n\nMax: 1,000 characters'
                )
                .min(1)
                .max(1000)
                .optional(),
            })
          )
          .min(0)
          .max(100)
          .optional(),
        coupon: z
          .object({
            code: z.string().describe('Coupon code.').optional(),
            _id: z
              .string()
              .describe('Coupon ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
          })
          .describe('Coupon applied to the order.')
          .optional(),
        payments: z
          .array(
            z.object({
              type: z
                .enum([
                  'UNSPECIFIED_PAYMENT_TYPE',
                  'CASH',
                  'CREDIT',
                  'DELIVERY_CLUB',
                  'DELIVERY_COM',
                  'BITPAY',
                  'CELLARIX',
                  'BITSOFGOLD',
                  'MULTIPASS',
                  'TENBIS',
                  'PAYPAL',
                  'MYSODEXO',
                  'WIX_PAYMENTS',
                ])
                .describe('Payment type.')
                .optional(),
              amount: z
                .string()
                .describe(
                  'Amount paid using this payment type. Only differs from total amount paid in case of split payments.'
                )
                .optional()
                .nullable(),
              method: z
                .string()
                .describe('Payment method.')
                .optional()
                .nullable(),
              providerTransactionId: z
                .string()
                .describe(
                  'Transaction ID.\nSee [Cashier API](https://dev.wix.com/api/rest/wix-cashier/payments/transaction) for more details.'
                )
                .optional()
                .nullable(),
            })
          )
          .min(1)
          .max(100)
          .optional(),
        fulfillment: z
          .intersection(
            z.object({
              type: z
                .enum([
                  'UNSPECIFIED_FULFILLMENT_TYPE',
                  'PICKUP',
                  'DELIVERY',
                  'DINE_IN',
                ])
                .describe('Fulfillment type.')
                .optional(),
              promisedTime: z
                .date()
                .describe('Latest delivery time promised by the restaurant.')
                .optional()
                .nullable(),
              asap: z
                .boolean()
                .describe(
                  'Whether the order should be fulfilled as soon as possible. Defaults to `true`.'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                deliveryDetails: z.never().optional(),
                pickupDetails: z.never().optional(),
                dineInDetails: z.never().optional(),
              }),
              z.object({
                pickupDetails: z.never().optional(),
                dineInDetails: z.never().optional(),
                deliveryDetails: z
                  .intersection(
                    z.object({
                      address: z
                        .object({
                          formatted: z
                            .string()
                            .describe('Formatted delivery address.')
                            .max(250)
                            .optional()
                            .nullable(),
                          country: z
                            .string()
                            .describe('Country.')
                            .max(100)
                            .optional()
                            .nullable(),
                          city: z
                            .string()
                            .describe('City name.')
                            .max(100)
                            .optional()
                            .nullable(),
                          street: z
                            .string()
                            .describe('Street name.')
                            .max(100)
                            .optional()
                            .nullable(),
                          streetNumber: z
                            .string()
                            .describe('Street number.')
                            .max(100)
                            .optional()
                            .nullable(),
                          apt: z
                            .string()
                            .describe('Apartment number.')
                            .max(100)
                            .optional()
                            .nullable(),
                          floor: z
                            .string()
                            .describe('Floor.')
                            .max(100)
                            .optional()
                            .nullable(),
                          entrance: z
                            .string()
                            .describe('Entrance.')
                            .max(100)
                            .optional()
                            .nullable(),
                          zipCode: z
                            .string()
                            .describe('ZIP/postal code.')
                            .max(100)
                            .optional()
                            .nullable(),
                          countryCode: z
                            .string()
                            .describe(
                              'Country code in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.'
                            )
                            .optional()
                            .nullable(),
                          onArrival: z
                            .enum([
                              'UNSPECIFIED_ON_ARRIVAL_TYPE',
                              'BUZZ_DOOR',
                              'CALL_ME',
                            ])
                            .describe(
                              'Special delivery instructions upon arrival to the address.'
                            )
                            .optional(),
                          approximate: z
                            .boolean()
                            .describe(
                              'Whether an approximate address is used. Defaults to `false`. In case of `false` a house number is required.'
                            )
                            .optional()
                            .nullable(),
                          comment: z
                            .string()
                            .describe(
                              'Delivery Instructions added by the customer.'
                            )
                            .max(500)
                            .optional()
                            .nullable(),
                          location: z
                            .object({
                              latitude: z
                                .number()
                                .describe('Address latitude.')
                                .optional()
                                .nullable(),
                              longitude: z
                                .number()
                                .describe('Address longitude.')
                                .optional()
                                .nullable(),
                            })
                            .describe('Geo coordinates of the address.')
                            .optional(),
                          addressLine2: z
                            .string()
                            .describe('Address Line 2.')
                            .optional()
                            .nullable(),
                          subdivision: z
                            .string()
                            .describe(
                              'Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe('Delivery address.')
                        .optional(),
                      pickupInfo: z
                        .object({
                          windowStartTime: z
                            .date()
                            .describe(
                              'When a delivery is ready to be picked up. This is the start time of the pickup window.'
                            )
                            .optional()
                            .nullable(),
                          windowEndTime: z
                            .date()
                            .describe(
                              'When a delivery must be picked up by. This is the end time of the pickup window.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe('Information about the delivery pickup.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        restaurant: z.never().optional(),
                        externalProvider: z.never().optional(),
                      }),
                      z.object({
                        externalProvider: z.never().optional(),
                        restaurant: z
                          .object({
                            fee: z
                              .string()
                              .describe('Delivery fee.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Delivery through the restaurant.'),
                      }),
                      z.object({
                        restaurant: z.never().optional(),
                        externalProvider: z
                          .object({
                            _id: z
                              .string()
                              .describe('ID of the external delivery provider.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            name: z
                              .string()
                              .describe(
                                'Name of the external delivery provider.'
                              )
                              .optional(),
                            customerFee: z
                              .string()
                              .describe('Delivery fee charged to the customer.')
                              .optional()
                              .nullable(),
                            commission: z
                              .string()
                              .describe(
                                'Fee paid by the restaurant to the external delivery provider.'
                              )
                              .optional()
                              .nullable(),
                            pickupTime: z
                              .date()
                              .describe('Order pickup time.')
                              .optional()
                              .nullable(),
                            dropOffTime: z
                              .date()
                              .describe('Order drop off time.')
                              .optional()
                              .nullable(),
                          })
                          .describe('Delivery through an external provider.'),
                      }),
                    ])
                  )
                  .describe('Delivery details.'),
              }),
              z.object({
                deliveryDetails: z.never().optional(),
                dineInDetails: z.never().optional(),
                pickupDetails: z
                  .object({
                    fee: z
                      .string()
                      .describe('Pickup fee charged to the customer.')
                      .optional()
                      .nullable(),
                    curbside: z
                      .object({
                        info: z
                          .string()
                          .describe(
                            'Additional information for curbside pickup.'
                          )
                          .min(0)
                          .max(50)
                          .optional(),
                      })
                      .describe('Curbside pickup method')
                      .optional(),
                  })
                  .describe('Pickup details.'),
              }),
              z.object({
                deliveryDetails: z.never().optional(),
                pickupDetails: z.never().optional(),
                dineInDetails: z
                  .object({
                    label: z
                      .string()
                      .describe(
                        'Label of dine-in information added by the restaurant, e.g. `table` or `booth`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Value of dine-in information added by the restaurant, e.g. `#6`.'
                      )
                      .optional(),
                  })
                  .describe('Dine-in details'),
              }),
            ])
          )
          .describe('Order fulfillment information.')
          .optional(),
        customer: z
          .intersection(
            z.object({
              firstName: z.string().describe('First name.').optional(),
              lastName: z.string().describe('Last name.').optional(),
              phone: z.string().describe('Phone number.').optional().nullable(),
              email: z.string().describe('Email address.').optional(),
              contactId: z
                .string()
                .describe(
                  "Customer's contact ID.\nSee [Contacts API](https://dev.wix.com/api/rest/contacts/contacts/contacts-v4) for more details."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                memberId: z.never().optional(),
                visitorId: z.never().optional(),
              }),
              z.object({
                visitorId: z.never().optional(),
                memberId: z
                  .string()
                  .describe('Member ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                memberId: z.never().optional(),
                visitorId: z
                  .string()
                  .describe('Visitor ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe('Customer information.')
          .optional(),
        totals: z
          .object({
            subtotal: z
              .string()
              .describe(
                'Order subtotal. Calculated by substracting delivery fee, tax and discount from order total.'
              )
              .optional(),
            total: z.string().describe('Total order price.').optional(),
            delivery: z
              .string()
              .describe('Total delivery fees charged to the customer.')
              .optional()
              .nullable(),
            tax: z.string().describe('Total tax.').optional().nullable(),
            discount: z
              .string()
              .describe('Total discount amount.')
              .optional()
              .nullable(),
            loyaltySavings: z
              .string()
              .describe('Total amount saved through the Wix Loyalty program.')
              .optional()
              .nullable(),
            quantity: z
              .number()
              .int()
              .describe('Total number of line items.')
              .min(0)
              .optional(),
            tip: z.string().describe('Total tip.').optional().nullable(),
          })
          .describe('Order totals.')
          .optional(),
        activities: z
          .array(
            z.object({
              timestamp: z
                .date()
                .describe('Activity timestamp.')
                .optional()
                .nullable(),
              message: z
                .string()
                .describe('Optional message added during order activity.')
                .max(1000)
                .optional()
                .nullable(),
            })
          )
          .optional(),
        channelInfo: z
          .object({
            type: z
              .enum([
                'UNSPECIFIED_CHANNEL_TYPE',
                'WEB',
                'MOBILE_WEB',
                'MOBILE',
                'CALL_CENTER',
                'FACEBOOK',
                'TPA',
              ])
              .optional(),
          })
          .describe(
            'Information about the sales channel that submitted the order.'
          )
          .optional(),
        loyaltyInfo: z
          .object({
            rewardId: z
              .string()
              .describe('Associated Wix Loyalty reward ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            appliedAmount: z
              .string()
              .describe('Amount saved redeeming Wix Loyalty reward.')
              .optional()
              .nullable(),
            redeemedPoints: z
              .number()
              .int()
              .describe('Wix Loyalty points redeemed.')
              .min(0)
              .optional()
              .nullable(),
            transactionId: z
              .string()
              .describe('Associated Wix Loyalty transaction ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            estimatedAccountBalance: z
              .number()
              .int()
              .describe(
                'Wix Loyalty estimated account point balance.\nEqual to the sum of earned and adjusted points minus redeemed points.\nThe estimated point balance is the value before the order is completed. Following the purchase, the point balance is updated.'
              )
              .min(0)
              .optional()
              .nullable(),
            estimatedPointsEarned: z
              .number()
              .int()
              .describe(
                'Wix Loyalty estimated total earned points.\nThe estimated total earned points is the value before the order is completed. Following the purchase, the total is updated.'
              )
              .min(0)
              .optional()
              .nullable(),
            rewardRevision: z
              .string()
              .describe(
                'Associated Wix Loyalty reward revision number, which increments by 1 each time the loyalty account is updated.\n\nTo prevent conflicting changes, the current revision must be passed when updating the loyalty account.\nIgnored when creating an account.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Information about the order’s loyalty points.\nLearn more about the [Wix Loyalty Program](https://dev.wix.com/api/rest/wix-loyalty-program/introduction).'
          )
          .optional(),
      })
      .describe('Fulfilled order.')
      .optional(),
  });
})();
